<template>
                <div v-if="currentView === 'security_audit'">
                    <div class="flex items-center justify-between mb-4">
                        <div class="page-header"><div><div class="page-title">操作稽核</div><div class="page-subtitle">系統管理行為的完整操作日誌</div></div></div>
                    </div>
                    <div class="card p-6">
                        <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
                            <div class="flex flex-col gap-2 md:flex-row md:items-center">
                                <input v-model="securityAuditQuery" type="text" placeholder="搜尋：動作 / 帳號 / 目標 / 細節"
                                    class="input w-full md:w-80" @input="scheduleSecurityAuditSearch" @keyup.enter="resetSecurityAuditAndFetch" />
                                <input v-model="securityAuditSince" type="date" class="input w-full md:w-44"
                                    @change="resetSecurityAuditAndFetch" />
                                <input v-model="securityAuditUntil" type="date" class="input w-full md:w-44"
                                    @change="resetSecurityAuditAndFetch" />
                                <select v-model="securityAuditAction" class="input w-full md:w-56" @change="resetSecurityAuditAndFetch">
                                    <option value="">全部動作</option>
                                    <option value="backup_run">備份：立即備份</option>
                                    <option value="backup_download">備份：下載</option>
                                    <option value="phishing_scan_folder">詐騙：AI 掃描資料夾</option>
                                    <option value="phishing_scan_msg">詐騙：AI 掃描單封</option>
                                    <option value="phishing_quarantine_release">詐騙：釋放</option>
                                    <option value="phishing_quarantine_release_allowlist">詐騙：釋放+信任</option>
                                    <option value="phishing_quarantine_delete">詐騙：刪除</option>
                                    <option value="phishing_quarantine_download">詐騙：下載 EML</option>
                                    <option value="spam_scan_folder">垃圾：AI 掃描資料夾</option>
                                    <option value="spam_scan_msg">垃圾：AI 掃描單封</option>
                                    <option value="spam_quarantine_release">垃圾：釋放</option>
                                    <option value="spam_quarantine_release_allowlist">垃圾：釋放+允許</option>
                                    <option value="spam_quarantine_delete">垃圾：刪除</option>
                                    <option value="spam_quarantine_download">垃圾：下載 EML</option>
                                </select>
                                <select v-if="isSuperAdmin" v-model="securityAuditDomain" class="input w-full md:w-44"
                                    @change="resetSecurityAuditAndFetch">
                                    <option value="">全部網域</option>
                                    <option v-for="d in safeArr(domains)" :key="keyById(d, 'sec_audit_domain')" :value="d.name">{{ d.name }}</option>
                                </select>
                                <select v-model.number="securityAuditLimit" class="input w-full md:w-32"
                                    @change="resetSecurityAuditAndFetch">
                                    <option :value="20">每頁 20</option>
                                    <option :value="50">每頁 50</option>
                                    <option :value="100">每頁 100</option>
                                </select>
                            </div>
                            <div class="flex items-center gap-2 justify-end">
                                <button type="button" class="btn btn-sm btn-outline" @click="resetSecurityAuditAndFetch" :disabled="securityAuditLoading">
                                    {{ securityAuditLoading ? '載入中...' : '重新整理' }}
                                </button>
                                <button type="button" class="btn btn-sm btn-outline" @click="securityAuditPrevPage" :disabled="securityAuditLoading || securityAuditSkip <= 0">
                                    上一頁
                                </button>
                                <button type="button" class="btn btn-sm btn-outline" @click="securityAuditNextPage"
                                    :disabled="securityAuditLoading || (securityAuditSkip + securityAuditLimit) >= (securityAuditTotal || 0)">
                                    下一頁
                                </button>
                            </div>
                        </div>
                        <div v-if="securityAuditError" class="text-sm mb-3" style="color:var(--danger);">{{ securityAuditError }}</div>
                        <div class="text-xs mb-3" style="color:var(--gray-500);">
                            共 {{ securityAuditTotal || safeArr(securityAuditRecords).length }} 筆
                            <span v-if="securityAuditTotal">，第 {{ Math.floor((securityAuditSkip || 0) / (securityAuditLimit || 1)) + 1 }} / {{ Math.max(1, Math.ceil((securityAuditTotal || 0) / (securityAuditLimit || 1))) }} 頁</span>
                        </div>
                        <table class="data-table">
                            <thead>
                                <tr class="border-b" style="background:var(--gray-50);">
                                    <th class="p-2">日期時間</th>
                                    <th v-if="isSuperAdmin" class="p-2">網域</th>
                                    <th class="p-2">帳號</th>
                                    <th class="p-2">動作</th>
                                    <th class="p-2">目標</th>
                                    <th class="p-2">細節</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, idx) in safeArr(securityAuditRecords)" :key="keyById(row, 'sec_audit', idx)" class="border-b">
                                    <td class="p-2">{{ row.timestamp ? new Date(row.timestamp).toLocaleString() : '-' }}</td>
                                    <td v-if="isSuperAdmin" class="p-2">{{ row.domain || '-' }}</td>
                                    <td class="p-2">{{ row.actor_email || '-' }}</td>
                                    <td class="p-2">
                                        <span class="px-2 py-0.5 rounded text-xs" style="background:var(--gray-100);color:var(--gray-800);">{{ row.action || '-' }}</span>
                                    </td>
                                    <td class="p-2">
                                        <span v-if="row.entity_type || row.entity_id" class="text-xs" style="color:var(--gray-700);">
                                            {{ row.entity_type || '-' }} {{ row.entity_id || '' }}
                                        </span>
                                        <span v-else style="color:var(--gray-400);">-</span>
                                    </td>
                                    <td class="p-2 text-xs whitespace-pre-wrap" style="color:var(--gray-700);">{{ securityAuditDetailText(row) }}</td>
                                </tr>
                                <tr v-if="!securityAuditLoading && safeArr(securityAuditRecords).length === 0">
                                    <td :colspan="isSuperAdmin ? 6 : 5" class="p-4 text-center" style="color:var(--gray-500);">找不到紀錄。</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Logs View -->

</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()

// Spread all store state as reactive refs accessible in template
const stateRefs = storeToRefs(store)
// Destructure - Vue compiler exposes all variables declared in <script setup> to template
const {
  // Core state
  token, currentView, currentUser, loginForm, loginError,
  branding, notifications, showNotifPanel,
  // Command palette
  showCmdPalette, cmdQuery, cmdResults, cmdGrouped, cmdDefaults, cmdSelIdx,
  // Domain / Users / Departments
  domains, domainForm, showDomainForm, domainLoading,
  users, userForm, showUserForm, userLoading, userError,
  departments, deptForm, showDeptForm, deptLoading,
  // Contacts / Groups
  contacts, contactForm, showContactForm, contactLoading,
  groups, groupForm, showGroupForm, groupLoading,
  // Mail
  mailQueues, mailQueueLoading, mailQueueFilter,
  serviceStatus, serviceLoading,
  // Invoice Drive
  invoiceDriveState,
  // Quotes / Products
  quotes, quoteForm, showQuoteForm, quoteLoading,
  products, productForm, showProductForm, productLoading,
  priceBooks, priceBookForm, showPriceBookForm,
  // SSL
  sslCerts, sslLoading, sslDomain,
  // Security
  securityWizardStep, spamStats,
  securityFilterState,
  // Server settings
  serverMailSettings, serverDnsSettings, serverDbSettings, serverAiSettings,
  backupSettings,
  // Logs / Audit
  logs, logFilter, logLoading,
  mailAuditRecords, auditFilter, auditLoading,
  securityAuditLogs, securityAuditFilter,
  logAnalysisResult, logAnalysisLoading,
  // AI / RAG / Sales
  ragState, aiSalesState, aiMarketingState,
  // LINE
  lineSupportState,
  // Webmail
  webmailState, webmailAutoScanInvoices, webmailAutoScanMarkRead,
  // Unreplied
  unrepliedEmails, unrepliedLoading,
  // CBI
  cbiInsights, cbiLoading,
  // Misc
  sidebarCollapsed, sidebarMobile,
  journeyEnrollments, journeyEnrollmentsLoading,
  journeyFunnel, journeyFunnelLoading, journeyFunnelFilters,
  showJourneyEditorModal, showJourneyFunnelModal, showJourneyEnrollmentsModal,
  selectedJourneyForEnrollments, selectedJourneyForFunnel,
  journeyEditorForm,
  securityAuditQuery, securityAuditSince, securityAuditUntil,
  securityAuditDomain, securityAuditAction,
} = stateRefs

// Methods are accessed directly from store (not reactive, no storeToRefs needed)
const {
  login, logout, switchSidebarView, canAccess,
  openCommandPalette, closeCmdPalette, updateCmdResults, execCmdItemById,
  moveCmdSel, toggleNotifPanel, markNotifRead, clearNotifs,
  fetchDomains, saveDomain, deleteDomain,
  fetchUsers, saveUser, deleteUser, resetUserPassword,
  fetchDepartments, saveDepartment, deleteDepartment,
  fetchContacts, saveContact, deleteContact, importContacts,
  fetchGroups, saveGroup, deleteGroup,
  fetchMailQueue, releaseMailQueueItem, deleteMailQueueItem, flushMailQueue,
  fetchServiceStatus, fetchSpamStats,
  fetchSslCerts, issueSslCert, deleteSslCert,
  fetchProducts, saveProduct, deleteProduct,
  fetchPriceBooks, savePriceBook, deletePriceBook,
  fetchQuotes, saveQuote, deleteQuote, sendQuote,
  fetchLogs, fetchLogAnalysis,
  fetchMailAuditRecords, fetchSecurityAuditLogs,
  fetchServerMailSettings, saveServerMailSettings,
  fetchServerDnsSettings, saveServerDnsSettings,
  fetchServerDbSettings, saveServerDbSettings,
  fetchServerAiSettings, saveServerAiSettings,
  fetchBackupSettings, saveBackupSettings, triggerBackup,
  fetchSecurityFilter, saveSecurityFilter,
  fetchRagDocuments, uploadRagDocument, deleteRagDocument, queryRag,
  fetchUnreplied,
  fetchCbiInsights,
  loadBrandingFromLocal, saveBranding,
  safeArr, keyById,
  invoiceDriveCanGotoAdjacent, invoiceDriveGotoAdjacent,
  openInvoiceDetail, closeInvoiceDetail,
  resetSecurityAuditAndFetch, securityAuditPrevPage, securityAuditNextPage,
  scheduleSecurityAuditSearch,
} = store
</script>

